import api_base from './base'
import type { DocumentTemplate, TemplateVariable, TemplateVariableCreate, PaginatedTemplateVariables, TemplateVariableListParams } from '@/types/document-template'

export const create_cabinet_template = async (templateData: {
  slug: string
  title: string
  engine: 'html' | 'word'
  html_body?: string
  category?: string | null
  page_layout?: string | null
  file?: File
}): Promise<DocumentTemplate> => {
  try {
    const formData = new FormData()
    formData.append('slug', templateData.slug)
    formData.append('title', templateData.title)
    formData.append('engine', templateData.engine)
    formData.append('is_active', 'true')
    if (templateData.html_body) formData.append('html_body', templateData.html_body)
    if (templateData.category) formData.append('category', templateData.category)
    if (templateData.page_layout) formData.append('page_layout', templateData.page_layout)
    if (templateData.file) formData.append('word_file', templateData.file)
    const response = await api_base.post('/api/v1/document-templates/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }
    throw err?.response?.data ?? error
  }
}

export const update_cabinet_template = async (
  slug: string,
  templateData: {
    title?: string
    is_active?: boolean
    html_body?: string
  }
): Promise<DocumentTemplate> => {
  try {
    const response = await api_base.patch(`/api/v1/document-templates/${slug}/`, templateData)
    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }
    throw err?.response?.data ?? error
  }
}

export const delete_cabinet_template = async (slug: string): Promise<void> => {
  try {
    await api_base.delete(`/api/v1/document-templates/${slug}/`)
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }
    throw err?.response?.data ?? error
  }
}

export const validate_word_template_cabinet = async (file: File): Promise<{
  valid: boolean
  variables: string[]
  valid_variables?: string[]
  invalid_variables?: string[]
  message?: string
  errors?: string[]
}> => {
  const formData = new FormData()
  formData.append('file', file)
  try {
    const response = await api_base.post('/api/v1/document-templates/validate-word/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      validateStatus: () => true,
    })
    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }
    if (err?.response?.data && typeof err.response.data === 'object' && 'valid' in (err.response.data as object)) {
      return err.response.data as any
    }
    throw err?.response?.data ?? error
  }
}

export const preview_pdf_cabinet_template = async (slug: string): Promise<{ preview_pdf_url: string }> => {
  try {
    const response = await api_base.post(`/api/v1/document-templates/${slug}/preview-pdf/`, {})
    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }
    throw err?.response?.data ?? error
  }
}

export const list_cabinet_template_variables = async (
  params: TemplateVariableListParams = {}
): Promise<PaginatedTemplateVariables> => {
  try {
    const response = await api_base.get('/api/v1/template-variables/', { params })
    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }
    throw err?.response?.data ?? error
  }
}

export const get_available_variable_keys_cabinet = async (): Promise<TemplateVariable[]> => {
  try {
    const response = await api_base.get('/api/v1/template-variables/available/')
    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }
    throw err?.response?.data ?? error
  }
}

export const create_cabinet_template_variable = async (variable: TemplateVariableCreate): Promise<TemplateVariable> => {
  try {
    const response = await api_base.post('/api/v1/template-variables/', variable)
    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }
    throw err?.response?.data ?? error
  }
}

export const update_cabinet_template_variable = async (
  id: string,
  variable: Partial<TemplateVariableCreate>
): Promise<TemplateVariable> => {
  try {
    const response = await api_base.patch(`/api/v1/template-variables/${id}/`, variable)
    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }
    throw err?.response?.data ?? error
  }
}

export const delete_cabinet_template_variable = async (id: string): Promise<void> => {
  try {
    await api_base.delete(`/api/v1/template-variables/${id}/`)
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }
    throw err?.response?.data ?? error
  }
}

export const list_cabinet_templates = async (
  params: { 
    q?: string | null
    category_id?: string | null
    limit?: number
    offset?: number
    ordering?: string | null
    favorites_only?: boolean | null
  } = {}
): Promise<{
  count: number
  next: string | null
  previous: string | null
  results: DocumentTemplate[]
}> => {
  try {
    const response = await api_base.get('/api/v1/document-templates/', { params })
    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }
    throw err?.response?.data ?? error
  }
}
export const get_cabinet_template = async (slug: string, context?: Record<string, unknown>): Promise<DocumentTemplate> => {
  try {
    const params = context ? { context: JSON.stringify(context) } : {}
    const response = await api_base.get(`/api/v1/document-templates/${slug}/`, { params })
    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }
    throw err?.response?.data ?? error
  }
}

export const render_cabinet_template = async (
  slug: string,
  payload: Record<string, unknown>
): Promise<{
  id: string
  status: string
  document: {
    id: string
    slug: string
    file_path: string
    file_size: number
    format: string
    created_at: number
  }
}> => {
  try {
    const response = await api_base.post(`/api/v1/document-templates/${slug}/render/`, {
      payload
    })
    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }
    throw err?.response?.data ?? error
  }
}


export const list_template_categories = async (
  params: { 
    search?: string | null
    ordering?: string | null
  } = {}
): Promise<Array<{
  id: string
  name: string
  name_ar?: string
  description?: string
  is_active: boolean
  created_at: number
  updated_at: number
}>> => {
  try {
    const response = await api_base.get('/api/v1/template-variables/categories/', { params })
    return Array.isArray(response.data) ? response.data : (response.data.results || [])
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }
    throw err?.response?.data ?? error
  }
}

export const add_template_favorite = async (templateId: string): Promise<void> => {
  try {
    await api_base.post(`/api/v1/template-favorites/${templateId}/`)
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }
    throw err?.response?.data ?? error
  }
}

export const remove_template_favorite = async (templateId: string): Promise<void> => {
  try {
    await api_base.delete(`/api/v1/template-favorites/${templateId}/`)
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown } }
    throw err?.response?.data ?? error
  }
}
